import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/i18n";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Printer, X } from "lucide-react";

const LINE = "inline-block border-b border-slate-400 align-baseline";

export const WorksheetDialog = ({ open, onOpenChange, payload }) => {
  const { t, lang } = useI18n();
  const [sheet, setSheet] = useState(null);
  const [withAnswers, setWithAnswers] = useState(false);
  const [busy, setBusy] = useState(false);
  const lbl = (es, en) => (lang === "en" ? (en || es) : es);

  const onOpen = (v) => {
    onOpenChange(v);
    if (!v) setSheet(null);
  };

  useEffect(() => {
    if (!open) return;
    setSheet(null); setWithAnswers(false);
    setBusy(true);
    api.post("/conjugation/practice/worksheet", payload)
      .then((r) => setSheet(r.data))
      .catch((e) => {
        toast.error(e?.response?.data?.detail || t("cj_cannot_build"));
        onOpenChange(false);
      })
      .finally(() => setBusy(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const blank = (w = "w-32") => <span className={`${LINE} ${w} h-5`} />;

  const item = (it) => {
    if (!it.text) {
      return (
        <p className="text-[15px] leading-loose">
          <strong>{it.hint}</strong> · {lbl(it.tense_label, it.tense_label_en)} · {it.person_label}
          {" → "}{blank("w-44")}
        </p>
      );
    }
    const partes = it.text.split("____");
    return (
      <>
        <p className="text-[15px] leading-loose">
          {partes.map((p, i) => (
            <span key={i}>
              {p}
              {i < partes.length - 1 && blank(partes.length > 2 ? "w-24" : "w-32")}
            </span>
          ))}
        </p>
        <p className="text-xs text-slate-500 mt-0.5">
          ({it.hint}{it.hint2 ? ` / ${it.hint2}` : ""}
          {it.person_note ? ` — ${it.person_note}` : ""})
        </p>
      </>
    );
  };

  const answer = (it) => [
    it.answer_pronoun ? `${it.answer_pronoun} ${it.answer}` : it.answer,
    it.connector, it.answer2,
  ].filter(Boolean).join("  ·  ");

  return (
    <Dialog open={open} onOpenChange={onOpen}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto print:max-w-none print:max-h-none print:overflow-visible print:shadow-none print:border-0">
        <div className="flex items-center gap-3 mb-4 no-print" data-testid="worksheet-toolbar">
          <DialogTitle className="text-lg font-semibold">{t("cj_worksheet")}</DialogTitle>
          <div className="flex items-center gap-2 ml-auto">
            {sheet?.with_answers && (
              <>
                <Switch id="ws-ans" checked={withAnswers} onCheckedChange={setWithAnswers}
                        data-testid="worksheet-answers-switch" />
                <Label htmlFor="ws-ans" className="text-xs">{t("cj_worksheet_answers")}</Label>
              </>
            )}
            <Button size="sm" onClick={() => window.print()} disabled={!sheet}
                    data-testid="worksheet-print-btn">
              <Printer className="h-4 w-4" /> {t("cj_worksheet_print")}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => onOpen(false)}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {busy && <p className="text-sm text-slate-500">…</p>}
        {sheet && (
          <div className="worksheet bg-white text-slate-900" data-testid="worksheet-sheet">
            <div className="border-b-2 border-slate-800 pb-3 mb-5">
              <p className="text-xs uppercase tracking-[0.2em] text-slate-500">We Speak Spanish</p>
              <h1 className="text-2xl font-bold">{t("cj_worksheet")}</h1>
              <p className="text-xs text-slate-500 mt-1">
                {sheet.tenses.map((tn) => t(`cj_t_${tn}`)).join(" · ")}
                {sheet.level ? ` · ${t(`cj_level_${sheet.level}`)}` : ""}
                {` · ${sheet.total} ${t("cj_worksheet_items")}`}
              </p>
              <div className="flex gap-8 mt-4 text-sm">
                <span>{t("cj_worksheet_name")}: {blank("w-56")}</span>
                <span>{t("cj_worksheet_date")}: {blank("w-32")}</span>
              </div>
            </div>
            <ol className="space-y-5">
              {sheet.items.map((it) => (
                <li key={it.n} className="flex gap-3" data-testid={`worksheet-item-${it.n}`}>
                  <span className="text-slate-400 font-semibold w-6 shrink-0">{it.n}.</span>
                  <div className="flex-1">{item(it)}</div>
                </li>
              ))}
            </ol>
            {withAnswers && sheet.with_answers && (
              <div className="mt-10 pt-5 border-t-2 border-dashed border-slate-400 break-before-page"
                   data-testid="worksheet-answer-key">
                <h2 className="text-base font-bold mb-3">{t("cj_worksheet_key")}</h2>
                <ol className="grid grid-cols-2 gap-x-8 gap-y-1 text-sm">
                  {sheet.items.map((it) => (
                    <li key={it.n} className="flex gap-2">
                      <span className="text-slate-400 w-6 shrink-0">{it.n}.</span>
                      <strong>{answer(it)}</strong>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
